import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/auth';

const AuthContext = createContext();

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN_START':
            return {
                ...state,
                loading: true,
                error: null
            };
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                loading: false,
                isAuthenticated: true,
                user: action.payload.user,
                token: action.payload.access_token,
                error: null
            };
        case 'LOGIN_FAILURE':
            return {
                ...state,
                loading: false,
                isAuthenticated: false,
                user: null,
                token: null,
                error: action.payload
            };
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                token: null,
                error: null
            };
        case 'SET_USER':
            return {
                ...state,
                user: action.payload,
                isAuthenticated: true
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null
            };
        default:
            return state;
    }
};

const initialState = {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,  // Empezar en true para esperar verificación inicial
    error: null
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Verificar autenticación al cargar
    useEffect(() => {
        const checkAuth = () => {
            try {
                if (authService.isAuthenticated()) {
                    const user = authService.getStoredUser();
                    const token = authService.getToken();

                    if (user && token) {
                        dispatch({
                            type: 'LOGIN_SUCCESS',
                            payload: { user, access_token: token }
                        });
                        return;
                    }
                }
            } catch (error) {
                console.error('Error checking auth:', error);
            }
            // Si no hay autenticación válida o hubo error, terminar loading
            dispatch({ type: 'SET_LOADING', payload: false });
        };

        // Ejecutar verificación
        checkAuth();

        // Timeout de seguridad: si después de 3 segundos sigue en loading, terminarlo
        const timeout = setTimeout(() => {
            dispatch({ type: 'SET_LOADING', payload: false });
        }, 3000);

        return () => clearTimeout(timeout);
    }, []);

    const login = async (credentials) => {
        try {
            dispatch({ type: 'LOGIN_START' });
            const response = await authService.login(credentials);
            dispatch({ type: 'LOGIN_SUCCESS', payload: response });
            return response;
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Error al iniciar sesión';
            dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
            throw error;
        }
    };

    const register = async (userData) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const response = await authService.register(userData);
            dispatch({ type: 'SET_LOADING', payload: false });
            return response;
        } catch (error) {
            dispatch({ type: 'SET_LOADING', payload: false });
            const errorMessage = error.response?.data?.detail || 'Error al registrarse';
            dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
            throw error;
        }
    };

    const logout = () => {
        authService.logout();
        dispatch({ type: 'LOGOUT' });
    };

    const clearError = () => {
        dispatch({ type: 'CLEAR_ERROR' });
    };

    // Función para verificar si el usuario es admin
    const isAdmin = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'admin';
    };

    // Función para verificar si el usuario tiene rol XD
    const isXD = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'xd';
    };

    // Función para verificar si el usuario tiene rol lector
    const isLector = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'lector';
    };

    // Función para verificar si el usuario tiene rol master
    const isMaster = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'master';
    };

    // Función para verificar si el usuario tiene rol sst
    const isSST = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'sst';
    };

    // Función para verificar si el usuario tiene rol validador (aprueba/rechaza sesiones)
    const isValidador = () => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === 'validador';
    };

    // Validador o admin pueden aprobar/rechazar sesiones
    const puedeValidar = () => isAdmin() || isValidador();

    // Función para verificar si el usuario tiene un rol específico
    const hasRole = (role) => {
        const userRole = state.user?.role?.toLowerCase();
        return userRole === role.toLowerCase() || userRole === 'admin';
    };

    const value = {
        ...state,
        login,
        register,
        logout,
        clearError,
        isAdmin,
        isXD,
        isLector,
        isMaster,
        isSST,
        isValidador,
        puedeValidar,
        hasRole
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
};